import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import SEO from '../components/common/SEO';
import { detectPlatform } from '../utils/detectPlatform';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  APP_STORE_BADGE,
  GOOGLE_PLAY_BADGE,
} from '../constants/appStoreLinks';

/**
 * A single shareable link (nemlingo.com/get) that sends each visitor to the
 * right place: iOS to the App Store, Android to Google Play, everyone else to
 * the /download page and its QR codes.
 */
const GetAppPage: React.FC = () => {
  const platform = detectPlatform(navigator.userAgent, navigator.maxTouchPoints);

  useEffect(() => {
    if (platform === 'ios') {
      // replace(), not href: leaves no history entry, so Back from the store
      // does not bounce the visitor straight forward again.
      window.location.replace(APP_STORE_URL);
    } else if (platform === 'android') {
      window.location.replace(PLAY_STORE_URL);
    }
  }, [platform]);

  if (platform === 'other') {
    return <Navigate to="/download" replace />;
  }

  const heading =
    platform === 'ios' ? 'Opening the App Store…' : 'Opening Google Play…';

  // Normally on screen for a moment. But in-app webviews (Facebook, Instagram,
  // LinkedIn) sometimes block the redirect above, and then this is all the
  // visitor ever sees — so it has to work as a standalone download page.
  return (
    <>
      <SEO
        title="Get Nemlingo"
        description="Download Nemlingo for iOS and Android."
        canonicalUrl="https://nemlingo.com/download"
        noindex
      />
      <main className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-primary">
            {heading}
          </h1>
          <p className="mt-3 text-gray-600">If nothing happens, tap below.</p>

          <div className="mt-8 flex flex-col items-center gap-4">
            <a
              href={APP_STORE_URL}
              aria-label="Download on the App Store"
              className="transition-transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg"
            >
              <img
                src={APP_STORE_BADGE}
                alt="Download on the App Store"
                className="h-[60px] w-[180px] object-contain"
              />
            </a>

            <a
              href={PLAY_STORE_URL}
              aria-label="Get it on Google Play"
              className="transition-transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg"
            >
              <img
                src={GOOGLE_PLAY_BADGE}
                alt="Get it on Google Play"
                className="h-[60px] w-[180px] object-contain"
              />
            </a>
          </div>
        </div>
      </main>
    </>
  );
};

export default GetAppPage;
